export interface EditorCommand {
  /** Discriminator for coalescing ('AddObstacle', 'MoveObstacle', ...) */
  kind: string;
  /** Key identifying the target object so commands on different targets never coalesce */
  targetKey: string;
  do(): void;
  undo(): void;
  /**
   * If this command can absorb `next`, mutate itself to include next's effect
   * (do() will have already run for `next` by the caller) and return true.
   * Return false to record `next` as a new history entry.
   */
  coalesceWith?(next: EditorCommand): boolean;
}

const MAX_HISTORY = 100;
const COALESCE_WINDOW_MS = 500;

/**
 * Internal wrapper that groups a root command with all commands it has
 * coalesced. undo() runs all absorbed commands' undos in reverse order,
 * then the root's undo — ensuring the full accumulated effect is reversed
 * even when the root command's closure only tracks its own contribution.
 */
class CoalescedEntry implements EditorCommand {
  readonly kind: string;
  readonly targetKey: string;
  private root: EditorCommand;
  /** Absorbed commands in execution order (not including root) */
  private absorbed: EditorCommand[] = [];

  constructor(root: EditorCommand) {
    this.root = root;
    this.kind = root.kind;
    this.targetKey = root.targetKey;
  }

  do(): void {
    this.root.do();
    for (const c of this.absorbed) c.do();
  }

  undo(): void {
    // Undo in reverse execution order
    for (let i = this.absorbed.length - 1; i >= 0; i--) {
      this.absorbed[i].undo();
    }
    this.root.undo();
  }

  coalesceWith(next: EditorCommand): boolean {
    if (!this.root.coalesceWith) return false;
    if (this.root.coalesceWith(next)) {
      this.absorbed.push(next);
      return true;
    }
    return false;
  }
}

export class History {
  private stack: CoalescedEntry[] = [];
  private redoStack: CoalescedEntry[] = [];
  private lastExecTs = 0;

  execute(cmd: EditorCommand): void {
    cmd.do();
    const now = Date.now();
    const last = this.stack[this.stack.length - 1];
    const canCoalesce =
      !!last &&
      last.kind === cmd.kind &&
      last.targetKey === cmd.targetKey &&
      now - this.lastExecTs <= COALESCE_WINDOW_MS;
    if (canCoalesce && last.coalesceWith(cmd)) {
      this.lastExecTs = now;
      this.redoStack.length = 0;
      return;
    }
    const entry = new CoalescedEntry(cmd);
    this.stack.push(entry);
    if (this.stack.length > MAX_HISTORY) this.stack.shift();
    this.redoStack.length = 0;
    this.lastExecTs = now;
  }

  undo(): void {
    const entry = this.stack.pop();
    if (!entry) return;
    entry.undo();
    this.redoStack.push(entry);
  }

  redo(): void {
    const entry = this.redoStack.pop();
    if (!entry) return;
    entry.do();
    this.stack.push(entry);
  }

  canUndo(): boolean { return this.stack.length > 0; }
  canRedo(): boolean { return this.redoStack.length > 0; }

  clear(): void {
    this.stack.length = 0;
    this.redoStack.length = 0;
    this.lastExecTs = 0;
  }
}
